<template>
  <form class="applicant-form" @submit.prevent>
    <div class="field">
      <div class="field-header">
        <label class="field-label" for="contest-entry-name">
          {{ t("contestEntryForm.step1.name.label") }}
          <span class="required-mark" aria-hidden="true">*</span>
        </label>
        <span class="field-count">{{ value.name.length }}/100</span>
      </div>
      <input
        id="contest-entry-name"
        :value="value.name"
        class="field-input"
        type="text"
        maxlength="100"
        :placeholder="t('contestEntryForm.step1.name.placeholder')"
        @input="handlers.onNameInput"
      />
    </div>

    <div class="field">
      <div class="field-header">
        <label class="field-label" for="contest-entry-name-kana">
          {{ t("contestEntryForm.step1.nameKana.label") }}
          <span class="required-mark" aria-hidden="true">*</span>
        </label>
        <span class="field-count">{{ value.nameKana.length }}/100</span>
      </div>
      <input
        id="contest-entry-name-kana"
        :value="value.nameKana"
        class="field-input"
        type="text"
        maxlength="100"
        :placeholder="t('contestEntryForm.step1.nameKana.placeholder')"
        @input="handlers.onNameKanaInput"
      />
    </div>

    <div class="field">
      <label class="field-label" for="contest-entry-penname">
        {{ t("contestEntryForm.step1.penname.label") }}
        <span class="required-mark" aria-hidden="true">*</span>
      </label>
      <div ref="pennameFieldRef" class="select-wrapper" :class="{ 'is-open': isPennameMenuOpen }">
        <div class="penname-control">
          <button
            id="contest-entry-penname"
            type="button"
            class="field-input field-select penname-trigger"
            :aria-expanded="isPennameMenuOpen"
            aria-haspopup="listbox"
            aria-controls="contest-entry-penname-list"
            @click="togglePennameMenu"
          >
            <span class="penname-trigger-text">{{ value.penname }}</span>
          </button>
          <IconChevronDown class="select-chevron" />
        </div>
        <div v-if="isPennameMenuOpen" id="contest-entry-penname-list" class="penname-menu">
          <div class="penname-options" role="listbox" :aria-label="t('contestEntryForm.step1.penname.label')">
            <button
              v-for="name in pennameOptions"
              :key="name"
              type="button"
              class="penname-option"
              role="option"
              :aria-selected="value.penname === name"
              @click="onSelectPenname(name)"
            >
              <IconCheck
                v-if="value.penname === name"
                class="penname-option-icon penname-option-check"
                aria-hidden="true"
              />
              <span v-else class="penname-option-spacer" aria-hidden="true"></span>
              <span class="penname-option-label">{{ name }}</span>
            </button>
          </div>
          <button type="button" class="penname-option" @click="openAddPennameDialog">
            <IconPlus class="penname-option-icon" aria-hidden="true" />
            <span class="penname-option-label">{{ t("contestEntryForm.step1.penname.add") }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="field">
      <div class="field-header">
        <label class="field-label" for="contest-entry-email">
          {{ t("contestEntryForm.step1.email.label") }}
          <span class="required-mark" aria-hidden="true">*</span>
        </label>
        <span class="field-count">{{ value.email.length }}/100</span>
      </div>
      <input
        id="contest-entry-email"
        :value="value.email"
        class="field-input"
        type="email"
        maxlength="100"
        :placeholder="t('contestEntryForm.step1.email.placeholder')"
        @input="handlers.onEmailInput"
      />
    </div>

    <div class="field">
      <div class="field-header">
        <label class="field-label" for="contest-entry-phone">
          {{ t("contestEntryForm.step1.phone.label") }}
          <span class="required-mark" aria-hidden="true">*</span>
        </label>
        <span class="field-count">{{ value.phone.length }}/100</span>
      </div>
      <input
        id="contest-entry-phone"
        :value="value.phone"
        class="field-input"
        type="tel"
        maxlength="100"
        :placeholder="t('contestEntryForm.step1.phone.placeholder')"
        @input="handlers.onPhoneInput"
      />
    </div>

    <div class="field">
      <label class="field-label" for="contest-entry-gender">{{ t("contestEntryForm.step1.gender.label") }}</label>
      <div class="select-wrapper">
        <select id="contest-entry-gender" v-model="genderSelectValue" class="field-input field-select">
          <option value="">{{ t("common.unselected") }}</option>
          <option v-for="option in genderOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <IconChevronDown class="select-chevron" />
      </div>
    </div>

    <div class="field">
      <label class="field-label" for="contest-entry-birthdate">
        {{ t("contestEntryForm.step1.birthDate.label") }}
        <span class="required-mark" aria-hidden="true">*</span>
      </label>
      <input
        id="contest-entry-birthdate"
        :value="value.birthDate"
        class="field-input"
        type="date"
        :placeholder="t('contestEntryForm.step1.birthDate.placeholder')"
        @input="handlers.onBirthDateInput"
      />
    </div>

    <div class="field">
      <div class="field-header">
        <label class="field-label" for="contest-entry-history-of-awards">
          {{ t("contestEntryForm.step1.historyOfAwards.label") }}
        </label>
        <span class="field-count">{{ value.historyOfAwards.length }}/{{ historyOfAwardsMaxLength }}</span>
      </div>
      <textarea
        id="contest-entry-history-of-awards"
        :value="value.historyOfAwards"
        class="field-input field-textarea"
        :maxlength="historyOfAwardsMaxLength"
        rows="5"
        :placeholder="t('contestEntryForm.step1.historyOfAwards.placeholder')"
        @input="handlers.onHistoryOfAwardsInput"
      />
    </div>

    <div v-if="isAddPennameOpen" class="penname-dialog-mask" @click.self="closeAddPennameDialog">
      <div class="penname-dialog" role="dialog" aria-modal="true" aria-labelledby="contest-entry-penname-dialog-title">
        <header class="penname-dialog-header">
          <h2 id="contest-entry-penname-dialog-title" class="penname-dialog-title">
            {{ t("contestEntryForm.step1.penname.dialogTitle") }}
          </h2>
          <button
            type="button"
            class="penname-dialog-close"
            :aria-label="t('common.close')"
            @click="closeAddPennameDialog"
          >
            <IconClose class="penname-dialog-close-icon" />
          </button>
        </header>
        <div class="penname-dialog-body">
          <div class="field-header">
            <label class="field-label" for="contest-entry-new-penname">
              {{ t("contestEntryForm.step1.penname.newLabel") }}
            </label>
            <span class="field-count">{{ newPenname.length }}/{{ pennameMaxLength }}</span>
          </div>
          <input
            id="contest-entry-new-penname"
            ref="newPennameInputRef"
            v-model="newPenname"
            class="field-input"
            type="text"
            :maxlength="pennameMaxLength"
            :placeholder="t('contestEntryForm.step1.penname.placeholder')"
            @keydown.enter.prevent="onConfirmAddPenname"
          />
        </div>
        <footer class="penname-dialog-footer">
          <button
            type="button"
            class="penname-dialog-button primary"
            :disabled="!canAddPenname"
            @click="onConfirmAddPenname"
          >
            {{ t("contestEntryForm.step1.penname.addAction") }}
          </button>
          <button type="button" class="penname-dialog-button" @click="closeAddPennameDialog">
            {{ t("common.cancel") }}
          </button>
        </footer>
      </div>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { computed, defineEmits, defineProps, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n-bridge";
import { Gender } from "@/lib/models/nolaContest";
import {
  CONTEST_ENTRY_HISTORY_OF_AWARDS_MAX_LENGTH,
  CONTEST_ENTRY_PENNAME_MAX_LENGTH,
} from "@/lib/utils/contestEntryApplicantForm";
import IconCheck from "~icons/figma/Check";
import IconChevronDown from "~icons/figma/ChevronDown";
import IconClose from "~icons/figma/Close";
import IconPlus from "~icons/figma/Plus";
import {
  createContestEntryApplicantInfoFormEventHandlers,
  type ContestEntryApplicantFormValue,
} from "./contestEntryApplicantInfoFormEventHandlers";

export type { ContestEntryApplicantFormValue };

const pennameMaxLength = CONTEST_ENTRY_PENNAME_MAX_LENGTH;
const historyOfAwardsMaxLength = CONTEST_ENTRY_HISTORY_OF_AWARDS_MAX_LENGTH;

const props = defineProps<{
  value: ContestEntryApplicantFormValue;
  pennameOptions: string[];
}>();

const emit = defineEmits<{
  (e: "input", value: ContestEntryApplicantFormValue): void;
  (e: "add-penname", name: string): void;
}>();

const { t } = useI18n();

function updateField<K extends keyof ContestEntryApplicantFormValue>(
  field: K,
  fieldValue: ContestEntryApplicantFormValue[K],
) {
  emit("input", { ...props.value, [field]: fieldValue });
}

const handlers = createContestEntryApplicantInfoFormEventHandlers(updateField);

const genderOptions = computed(() =>
  Object.entries(Gender)
    .filter(([_, genderValue]) => !Number.isNaN(Number(genderValue)))
    .map(([key, genderValue]) => ({
      value: String(genderValue),
      label: t(`common.gender.${key}`),
    })),
);

const pennameFieldRef = ref<HTMLElement | null>(null);
const newPennameInputRef = ref<HTMLInputElement | null>(null);
const isPennameMenuOpen = ref(false);
const isAddPennameOpen = ref(false);
const newPenname = ref("");

const canAddPenname = computed(() => newPenname.value.trim().length > 0);

function togglePennameMenu() {
  isPennameMenuOpen.value = !isPennameMenuOpen.value;
}

function onSelectPenname(name: string) {
  updateField("penname", name);
  isPennameMenuOpen.value = false;
}

function openAddPennameDialog() {
  isPennameMenuOpen.value = false;
  newPenname.value = "";
  isAddPennameOpen.value = true;
}

function closeAddPennameDialog() {
  isAddPennameOpen.value = false;
  newPenname.value = "";
}

function onConfirmAddPenname() {
  const name = newPenname.value.trim();
  if (!name) return;
  if (props.pennameOptions.includes(name)) {
    onSelectPenname(name);
  } else {
    emit("add-penname", name);
    isPennameMenuOpen.value = false;
  }
  closeAddPennameDialog();
}

function onDocumentPointerDown(event: Event) {
  if (!isPennameMenuOpen.value) return;
  const { target } = event;
  if (!(target instanceof Node)) return;
  if (pennameFieldRef.value?.contains(target)) return;
  isPennameMenuOpen.value = false;
}

function onDocumentKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  if (isAddPennameOpen.value) {
    closeAddPennameDialog();
    return;
  }
  isPennameMenuOpen.value = false;
}

watch(isAddPennameOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : "";
  if (!open) return;
  nextTick(() => {
    newPennameInputRef.value?.focus();
  });
});

onMounted(() => {
  document.addEventListener("pointerdown", onDocumentPointerDown);
  document.addEventListener("keydown", onDocumentKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onDocumentPointerDown);
  document.removeEventListener("keydown", onDocumentKeydown);
  document.body.style.overflow = "";
});

const genderSelectValue = computed({
  get: () => {
    const { gender } = props.value;
    if (gender === Gender.male || gender === Gender.female || gender === Gender.other) {
      return String(gender);
    }
    return "";
  },
  set: (selected: string) => {
    updateField("gender", selected ? Number(selected) : 0);
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.applicant-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.field-label {
  margin: 0 0 4px 4px;
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.field-header .field-label {
  margin-bottom: 0;
}

.required-mark {
  color: var(--color-warning);
}

.field-count {
  font-size: var(--font-size-xs);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.field-input {
  display: block;
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  border: none;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  line-height: var(--line-height-default);
  box-sizing: border-box;

  &::placeholder {
    color: var(--color-text-low);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.field-textarea {
  height: auto;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
}

.field-select {
  appearance: none;
  padding-right: 32px;
}

.select-wrapper {
  position: relative;
}

.select-wrapper.is-open {
  z-index: 2;
}

.select-chevron {
  position: absolute;
  top: 50%;
  right: 12px;
  font-size: 12px;
  color: var(--color-text-normal);
  pointer-events: none;
  transform: translateY(-50%);
}

.penname-control {
  position: relative;
}

.penname-trigger {
  display: flex;
  align-items: center;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.penname-trigger-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.penname-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  padding: 8px 0;
  border-radius: var(--radius-sm);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-md);
}

.penname-option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  font-family: inherit;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  text-align: left;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--color-focus-ring);
  }
}

.penname-option-icon {
  flex-shrink: 0;
  font-size: var(--font-size-md);
  color: var(--color-text-normal);
}

.penname-option-check {
  color: var(--color-success);
}

.penname-option-spacer {
  flex-shrink: 0;
  width: var(--font-size-md);
}

.penname-option-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.penname-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background-color: var(--color-overlay);
  backdrop-filter: var(--backdrop-blur-md);
}

.penname-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  border-radius: var(--radius-lg);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}

.penname-dialog-header {
  position: relative;
  flex-shrink: 0;
  padding: 20px 48px 0;
}

.penname-dialog-title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  text-align: center;
}

.penname-dialog-close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: var(--radius-xs);
  background: transparent;
  color: var(--color-text-normal);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.penname-dialog-close-icon {
  font-size: var(--font-size-md);
}

.penname-dialog-body {
  padding: 16px 16px 0;
}

.penname-dialog-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 16px 24px;
}

.penname-dialog-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-family: inherit;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &.primary {
    background-color: var(--color-primary);
    color: var(--color-text-on-fixed-bg);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;

    &::after {
      display: none;
    }
  }
}
</style>
